import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Pagination,
    Popover,
    Snackbar,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskSavedFilterService from '../../services/TaskSavedFilterService';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import TaskViewService from '../../services/TaskViewService';
import { PageHeader } from '../common/PageHeader';
import ListView from '../navigation/ListView';
import { refreshTaskSummary } from './useTaskSummary';
import { LiveLinkConfirm, SaveFilterDialog, ViewLinksDialog } from './SavedFilterDialogs';
import TaskActionDialog from './TaskActionDialog';
import TaskBoard from './TaskBoard';
import TaskCalendar from './TaskCalendar';
import NewPostDialog from './NewPostDialog';
import NewTaskDialog from './NewTaskDialog';
import PostingCalendar from './PostingCalendar';
import TaskDrawer from './TaskDrawer';
import TaskFiltersPanel from './TaskFiltersPanel';
import { monthEnd, monthGrid, monthKey, monthName, monthStart } from './calendarUi';
import { useTaskCategories, useTaskPlatforms } from './taskFields';
import {
    clearAllPatch,
    dateLabel,
    filterChips,
    matchesSaved,
    openSavedState,
    panelFields,
    removeChip,
    savedSections,
} from './taskFilters';
import { withAccessConfirmation } from './confirmAccess';
import { buildTaskUrl, filterFromUrl, parseTaskUrl, shortName } from './taskUi';
import useTaskSummary from './useTaskSummary';

const GROUP_LABELS = {
    none: 'No grouping',
    status: 'Status',
    stage: 'Stage',
    assignee: 'Assignee',
    file: 'File',
    pm: 'PM',
    language: 'Language',
    posting: 'Posting date',
    reason: 'Reason',
};

// What the board shows; one kind of work at a time
const BOARD_SCOPES = [
    { value: 'MEDIA', label: 'Videos and text', types: ['VIDEO', 'TEXT'] },
    { value: 'BOOK', label: 'Books', types: ['BOOK'] },
    { value: 'POST', label: 'Posts', types: ['POST'] },
    { value: 'AUDIO', label: 'Dubbing audio', types: ['AUDIO'] },
];

// Views that can be shown as a board (one column per stage)
const BOARD_VIEWS = ['pipeline'];
// Views with a "Show upcoming" switch: also list the stages each task hasn't reached
const UPCOMING_VIEWS = ['all', 'pipeline'];
// Reads its own data, by tab: List, Calendar or Post bank
const POSTING_VIEW = 'posting-calendar';
// The board shows everything in one go; the server caps a page at this many rows
const BOARD_SIZE = 200;

// Rail headings are labels, not links: small, uppercase and muted so they don't read like the views under them
const RAIL_HEADING = {
    backgroundColor: 'transparent',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: 1,
    color: 'text.secondary',
    pt: 2,
    pb: 0.75,
    cursor: 'default',
    userSelect: 'none',
};

// Kept outside the component: the dialog resets its values when fields changes
const DELETE_FIELDS = [
    { name: 'confirm', label: "I understand this can't be undone", type: 'checkbox', required: true },
];

const EMPTY = {
    mine: { title: 'Nothing assigned to you right now', message: 'Work handed to you shows up here.' },
    'sent-back-to-me': { title: 'Nothing has been sent back to you', message: '' },
    watching: { title: "You aren't watching any tasks", message: '' },
    all: {
        title: 'No tasks yet',
        message:
            'Start tracking from Files: use Manage task in a file\'s three dots menu, select files and choose Start tracking files, or tick Track this work when creating a file.',
    },
    'needs-attention': {
        title: 'Nothing needs your attention',
        message: 'Flagged, blocked, overdue and unassigned tasks show up here.',
    },
};

// Remembered in this browser; reading can fail in a private window, so each has a fallback
const remembered = (key, fallback) => {
    try {
        return localStorage.getItem(key) || fallback;
    } catch (e) {
        return fallback;
    }
};
const remember = (key, value) => {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        // remembering is a convenience
    }
};

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * /tasks: a left rail of views (lists and reports) and saved filters, and the selected view
 * rendered with the file list's ListView in task mode, as a board, or as a calendar. All state
 * lives in the URL so views are bookmarkable and emails can link straight to a task.
 */
const TasksPage = ({ darkMode, toggleDarkMode, themeVariant, setCanNavigateToEdit }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [newPostOpen, setNewPostOpen] = useState(false);
    const [newTaskOpen, setNewTaskOpen] = useState(false);
    const narrow = useMediaQuery(theme.breakpoints.down('md'));
    // A phone puts the page actions beside the View dropdown, which stands in for the title
    const phone = useMediaQuery(theme.breakpoints.down('sm'));
    const summary = useTaskSummary();
    const me = (() => {
        try {
            return (JSON.parse(localStorage.getItem('authUser')) || {}).email?.toLowerCase();
        } catch (e) {
            return null;
        }
    })();

    const urlState = useMemo(() => parseTaskUrl(location.search), [location.search]);
    const [views, setViews] = useState(null);
    const [landing, setLanding] = useState('mine');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState(urlState.q || '');
    const [drawerFocus, setDrawerFocus] = useState(null);
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();

    const [taskTypes, setTaskTypes] = useState(null);
    // Select mode (PM/Admin): pick tasks on this page to delete together
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [assigning, setAssigning] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [snack, setSnack] = useState(null);
    // Part 13: New and ⋯ are menus, and Display is a small panel like Filters
    const [newMenu, setNewMenu] = useState(null);
    const [moreMenu, setMoreMenu] = useState(null);
    const [displayAnchor, setDisplayAnchor] = useState(null);
    const displayButton = useRef(null);

    // Rev6 Part 4: filters, saved filters and the calendar
    const [facets, setFacets] = useState(null);
    const [facetsFailed, setFacetsFailed] = useState(false);
    const [filtersAnchor, setFiltersAnchor] = useState(null);
    const filtersButton = useRef(null);
    const [savedFilters, setSavedFilters] = useState([]);
    const [naming, setNaming] = useState(null);
    const [savedMenu, setSavedMenu] = useState(null);
    const [linksFor, setLinksFor] = useState(null);
    const [liveConfirm, setLiveConfirm] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [month, setMonth] = useState(() => monthKey(todayIso()));
    const [calendarMode, setCalendarModeState] = useState(() => remembered('tasks.calendarMode', 'month'));
    const [calendarData, setCalendarData] = useState(null);
    const monthSettled = useRef(false);

    const viewKey = urlState.view || landing;
    const view = views?.find((v) => v.viewKey === viewKey);
    const postingView = viewKey === POSTING_VIEW;
    // The last layout used on each view is remembered; the URL wins when it names one
    const layout = (() => {
        // It always opens on the list from today, unless a link or saved filter says otherwise
        if (postingView) return ['calendar', 'bank'].includes(urlState.layout) ? urlState.layout : urlState.date ? 'calendar' : 'list';
        const want = urlState.layout || remembered(`tasks.layout.${viewKey}`, 'list');
        if (want === 'board' && BOARD_VIEWS.includes(viewKey)) return 'board';
        if (want === 'calendar' && view?.calendar) return 'calendar';
        return 'list';
    })();
    const board = layout === 'board';
    const calendar = layout === 'calendar' && !postingView;
    useTrackUsage('TasksPage', 'screen', 'tasks');
    // On the page, not in each view: these unmount on a reload, which would count as another open
    useTrackUsage('TaskList', 'screen', 'tasks', !!view && layout === 'list' && !postingView);
    useTrackUsage('TaskBoard', 'screen', 'tasks', !!view && board);
    useTrackUsage('TaskCalendar', 'screen', 'tasks', !!view && calendar);
    // Both left the toolbar in Part 13; if either is used often it comes back
    useTrackUsage('TaskDisplayPanel', 'popover', 'tasks', !!displayAnchor);
    useTrackUsage('TaskSelectMode', 'mode', 'tasks', selectMode);
    // Someone without the posting date gets their calendar by due date, with no choice (D110)
    const noPostingDate = !!view && !view.schema.filters.includes('posting');
    const calendarOn = noPostingDate ? 'due' : urlState.on || view?.calendarOn || 'posting';
    // A PM looking at someone else's My tasks (opened from Workload)
    const viewingUser =
        viewKey === 'mine' && urlState.user && urlState.user.toLowerCase() !== me ? urlState.user : null;

    const setUrl = useCallback(
        (patch, { replace = false } = {}) => {
            const next = { ...urlState, ...patch };
            navigate({ pathname: '/tasks', search: buildTaskUrl(next) }, { replace });
        },
        [urlState, navigate],
    );

    useEffect(() => {
        TaskViewService.listViews()
            .then((data) => {
                setViews(data.views);
                setLanding(data.landing);
            })
            .catch((e) => setError(taskErrorMessage(e)));
    }, []);

    const loadFacets = useCallback(() => {
        setFacetsFailed(false);
        setFacets(null);
        TaskViewService.facets()
            .then(setFacets)
            .catch(() => setFacetsFailed(true));
    }, []);
    useEffect(() => {
        loadFacets();
    }, [loadFacets]);

    const loadSaved = useCallback(
        () =>
            TaskSavedFilterService.list()
                .then(setSavedFilters)
                .catch((e) => setError(taskErrorMessage(e))),
        [],
    );
    useEffect(() => {
        loadSaved();
    }, [loadSaved]);

    useEffect(() => {
        if (board && !taskTypes) {
            TaskTrackingService.taskTypes()
                .then(setTaskTypes)
                .catch((e) => setError(taskErrorMessage(e)));
        }
    }, [board, taskTypes]);

    // The board shows one kind of work at a time so book stages don't widen the video board.
    // Remembered per user (this browser).
    const [boardScope, setBoardScopeState] = useState(() => remembered('tasks.boardScope', 'MEDIA'));
    const setBoardScope = (scope) => {
        setBoardScopeState(scope);
        remember('tasks.boardScope', scope);
    };
    const boardTypes = BOARD_SCOPES.find((x) => x.value === boardScope)?.types || BOARD_SCOPES[0].types;

    const setCalendarMode = (mode) => {
        setCalendarModeState(mode);
        remember('tasks.calendarMode', mode);
    };
    const setLayout = (next) => {
        remember(`tasks.layout.${viewKey}`, next);
        setUrl({ layout: next, page: 0 });
    };

    // Keyed on the filter's text, so opening or closing the drawer (task in the URL) doesn't reload
    const urlFilterKey = JSON.stringify(filterFromUrl(urlState));
    const filter = useMemo(() => {
        const f = JSON.parse(urlFilterKey);
        if (!board) return f;
        // Books and posts are one content type each; videos and text are filtered after loading
        const contentType =
            boardTypes.length === 1 ? boardTypes[0] : boardTypes.includes(f.contentType) ? f.contentType : undefined;
        return { ...f, contentType, group: 'none', page: 0, size: BOARD_SIZE };
    }, [urlFilterKey, board, boardTypes]);

    // Only the latest request may set the result: a slow earlier one (e.g. with upcoming on)
    // must not overwrite the list after the filter changed
    const latestRequest = useRef(0);
    const [postingRefresh, setPostingRefresh] = useState(0);
    const [postingMatch, setPostingMatch] = useState(null);
    const [postBankCount, setPostBankCount] = useState(null);
    const reload = useCallback(async () => {
        if (!view) return;
        if (view.viewKey === POSTING_VIEW) {
            setPostingRefresh((n) => n + 1);
            return;
        }
        const request = ++latestRequest.current;
        setLoading(true);
        setError(null);
        try {
            if (calendar) {
                // The whole grid, with the days of the months around it
                const weeks = monthGrid(month);
                const { page, size, group, sort, direction, ...f } = filter;
                const data = await TaskViewService.getCalendar(view.viewKey, f, {
                    on: calendarOn,
                    from: weeks[0][0],
                    to: weeks[weeks.length - 1][6],
                });
                if (request === latestRequest.current) {
                    setCalendarData(data);
                    // The first month follows the server's today (India time), not this browser's
                    if (!monthSettled.current) {
                        monthSettled.current = true;
                        if (data.today && monthKey(data.today) !== month) setMonth(monthKey(data.today));
                    }
                }
            } else {
                const data = await TaskViewService.getView(view.viewKey, filter);
                if (request === latestRequest.current) setResult(data);
            }
        } catch (e) {
            if (request === latestRequest.current) setError(taskErrorMessage(e));
        } finally {
            if (request === latestRequest.current) setLoading(false);
        }
    }, [view, filter, calendar, month, calendarOn]);

    useEffect(() => {
        if (!postingView) reload();
    }, [reload, postingView]);

    // Another view's or saved filter's calendar mustn't show, dimmed, under this one's title
    useEffect(() => {
        setCalendarData(null);
    }, [viewKey, urlState.saved]);

    // A selection only covers rows on screen, so changing the view, filter or page clears it
    const filterKey = JSON.stringify(filter);
    useEffect(() => {
        setSelected(new Set());
    }, [viewKey, filterKey]);

    const toggleSelected = (id) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    const pageIds = [...new Set((result?.rows || []).map((r) => r.id))];
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    // A task can show on several rows (its current stage and each stage up next)
    const selectedRowCount = (result?.rows || []).filter((r) => selected.has(r.id)).length;
    const exitSelectMode = () => {
        setSelectMode(false);
        setSelected(new Set());
    };

    const deleteSelected = async () => {
        const res = await TaskTrackingService.removeBulk([...selected]);
        setConfirmDelete(false);
        const deleted = `Deleted ${res.deleted} task${res.deleted === 1 ? '' : 's'}.`;
        if (res.failed.length > 0) {
            setSelected(new Set(res.failed.map((f) => f.taskId)));
            setError(
                `${deleted} ${res.failed.length} couldn't be deleted and are still selected: ${res.failed[0].message}`,
            );
        } else {
            exitSelectMode();
            setSnack(deleted);
        }
        reload();
    };

    // The stages of the ticked tasks' types, in order, and everyone who can be picked
    const openAssign = async () => {
        try {
            const [types, users] = await Promise.all([
                taskTypes || TaskTrackingService.taskTypes(),
                TaskTrackingService.assignableUsers(),
            ]);
            if (!taskTypes) setTaskTypes(types);
            const keys = new Set((result?.rows || []).filter((r) => selected.has(r.id)).map((r) => r.taskTypeKey));
            const stages = new Map();
            types
                .filter((t) => keys.has(t.key))
                .flatMap((t) => t.stages || [])
                .filter((s) => !s.retired)
                .forEach((s) => stages.set(s.stageKey, s.displayName));
            setAssigning({
                users,
                stages: [...stages].map(([value, label]) => ({ value, label })),
            });
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const assignSelected = async (values) => {
        const res = await withAccessConfirmation((grant) =>
            TaskTrackingService.assignBulk({
                taskIds: [...selected],
                stageKey: values.stageKey,
                assignee: values.assignee,
                grantContentAccess: grant,
            }),
        );
        if (!res) return;
        setAssigning(null);
        const line =
            `Assigned ${res.changed} task${res.changed === 1 ? '' : 's'}.` +
            (res.leftAlone > 0 ? ` ${res.leftAlone} left as ${res.leftAlone === 1 ? 'it was' : 'they were'}.` : '');
        if (res.failed.length > 0) {
            setSelected(new Set(res.failed.map((f) => f.taskId)));
            setError(`${line} ${res.failed.length} couldn't be assigned and are still selected: ${res.failed[0].message}`);
        } else {
            exitSelectMode();
            setSnack(line);
        }
        reload();
    };

    // Debounced search box → URL
    useEffect(() => {
        const t = setTimeout(() => {
            if ((urlState.q || '') !== searchText) setUrl({ q: searchText || undefined, page: 0 }, { replace: true });
        }, 400);
        return () => clearTimeout(t);
    }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

    // Opening a saved filter or a link changes the search in the URL; keep the box in step
    useEffect(() => {
        setSearchText(urlState.q || '');
    }, [urlState.saved]); // eslint-disable-line react-hooks/exhaustive-deps

    // Dropping a card on another column opens the drawer to confirm the move
    const moveTask = (row, stageKey) => openTask(row, { action: { move: stageKey } });

    const openTask = (row, opts = {}) => {
        setDrawerFocus(opts.action || null);
        setUrl({ task: row.id });
    };

    const onSort = (col) => {
        const same = (urlState.sort || view?.schema.defaultSort) === col;
        const dir = urlState.direction || view?.schema.defaultDirection || 'asc';
        setUrl({ sort: col, direction: same && dir === 'asc' ? 'desc' : 'asc', page: 0 });
    };

    // Switching views drops the settings that only make sense in the old one
    const openView = (key) =>
        setUrl({
            view: key,
            saved: undefined,
            layout: undefined,
            on: undefined,
            page: 0,
            group: undefined,
            sort: undefined,
            direction: undefined,
            user: undefined,
            upcoming: undefined,
        });

    const openPersonTasks = (group) =>
        setUrl({
            view: 'mine',
            saved: undefined,
            user: group.key,
            page: 0,
            group: undefined,
            sort: undefined,
            direction: undefined,
            stage: undefined,
            assignee: undefined,
        });

    const sections = useMemo(() => {
        const list = views || [];
        return [
            { title: 'Mine', items: list.filter((v) => v.section === 'mine') },
            { title: 'Team', items: list.filter((v) => v.section === 'team') },
        ].filter((s) => s.items.length > 0);
    }, [views]);

    // ------------------------------------------------------------------ filters

    const fields = panelFields(view).filter((f) => !(board && f === 'contentType'));
    const lookups = useMemo(
        () => ({
            language: Object.fromEntries((facets?.languages || []).map((o) => [o.key, o.label])),
            stage: Object.fromEntries((facets?.stages || []).map((o) => [o.key, o.label])),
            category: Object.fromEntries((categories || []).map((c) => [c.key, c.label])),
            platform: Object.fromEntries((platforms || []).map((p) => [p.key, p.label])),
        }),
        [facets, categories, platforms],
    );
    const chips = filterChips(urlState, fields, lookups);
    const filterBarRef = useRef(null);
    const chipFocus = useRef(null);
    // A removed chip takes focus with it: move it to the next chip, else Filters
    useEffect(() => {
        if (chipFocus.current == null) return;
        const index = chipFocus.current;
        chipFocus.current = null;
        const left = filterBarRef.current?.querySelectorAll('[data-chip]') || [];
        (left[Math.min(index, left.length - 1)] || filtersButton.current)?.focus();
    }, [chips.length]);
    const removeFilterChip = (c, index) => {
        chipFocus.current = index;
        setUrl(removeChip(urlState, c));
    };
    const changeFilters = (patch) => setUrl({ ...patch, page: 0 }, { replace: true });
    const clearAll = () => setUrl(clearAllPatch(fields));

    // ------------------------------------------------------------------ saved filters

    const openSaved = savedFilters.find((f) => f.id === urlState.saved);
    const filterKeys = view?.schema.filters || [];
    // The screen as a saved filter would keep it, layout included
    const screenState = {
        ...urlState,
        layout: layout === 'list' ? undefined : layout,
        on: calendar ? calendarOn : urlState.on,
    };
    const edited = !!openSaved && !matchesSaved(screenState, openSaved, filterKeys);
    // Something worth keeping: filters, a search or a grouping. A layout alone doesn't count (Part 13),
    // though a saved filter still keeps the layout it was saved on
    const savable = !openSaved && (chips.length > 0 || !!urlState.q || !!urlState.group || !!urlState.on);
    const screenQuery = () => buildTaskUrl(screenState);
    const { mine: mySaved, team: teamSaved } = savedSections(savedFilters);

    const openSavedFilter = (f) => {
        const state = openSavedState(f);
        if (!state.layout) state.layout = 'list';
        if (state.layout !== 'list') remember(`tasks.layout.${f.viewKey}`, state.layout);
        navigate({ pathname: '/tasks', search: buildTaskUrl(state) });
    };

    // Changing a filter people outside watch through a link asks first
    const withLiveCheck = (f, action, run) => (f.liveLinks > 0 ? setLiveConfirm({ filter: f, action, run }) : run());

    const saveNew = async (name, shared) => {
        const created = await TaskSavedFilterService.create({
            name,
            viewKey,
            query: screenQuery(),
            sharedWithTeam: shared,
        });
        setNaming(null);
        await loadSaved();
        setUrl({ saved: created.id });
        setSnack(`Saved "${created.name}"`);
    };

    const updateSaved = (f) =>
        withLiveCheck(f, 'Update', async () => {
            try {
                await TaskSavedFilterService.update(f.id, { query: screenQuery(), revision: f.revision });
                await loadSaved();
                setSnack(`Updated "${f.name}"`);
            } catch (e) {
                setError(taskErrorMessage(e));
            }
        });

    const rename = async (name) => {
        const f = naming.filter;
        const run = async () => {
            await TaskSavedFilterService.update(f.id, { name, revision: f.revision });
            setNaming(null);
            await loadSaved();
        };
        if (f.liveLinks > 0) {
            setNaming(null);
            setLiveConfirm({
                filter: f,
                action: 'Rename',
                run: () => run().catch((e) => setError(taskErrorMessage(e))),
            });
        } else {
            await run();
        }
    };

    const toggleShared = (f) =>
        withLiveCheck(f, f.sharedWithTeam ? 'Stop sharing' : 'Share', async () => {
            try {
                await TaskSavedFilterService.update(f.id, { sharedWithTeam: !f.sharedWithTeam, revision: f.revision });
                await loadSaved();
                setSnack(f.sharedWithTeam ? `"${f.name}" is only yours now` : `"${f.name}" is shared with the team`);
            } catch (e) {
                setError(taskErrorMessage(e));
            }
        });

    const deleteSaved = async () => {
        const f = deleting;
        setDeleting(null);
        try {
            await TaskSavedFilterService.remove(f.id);
            await loadSaved();
            if (urlState.saved === f.id) setUrl({ saved: undefined });
            setSnack(`Deleted "${f.name}"`);
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const groupOptions = view ? ['none', ...view.schema.groupByOptions.filter((g) => g !== 'none')] : [];
    const defaultGroup = view?.schema.defaultGroupBy || 'none';
    const currentGroup = urlState.group || defaultGroup;
    // Display holds how the list is laid out; it counts what differs from this view's own default
    const showGroup = groupOptions.length > 1 && !board && !calendar && !postingView;
    const showUpcoming = UPCOMING_VIEWS.includes(viewKey) && !calendar;
    const displayCount =
        (showGroup && currentGroup !== defaultGroup ? 1 : 0) + (showUpcoming && urlState.upcoming ? 1 : 0);
    const canSelect = !!summary?.canManage && !board && !calendar && !postingView;
    const hasMore = !!summary?.canManage;
    const countText = result && !calendar && !postingView ? result.total : null;
    const pageTitle = viewingUser
        ? `Tasks for ${shortName(viewingUser)}`
        : openSaved?.name || view?.displayName || 'Tasks';
    const pageCount = result && !board ? Math.ceil(result.total / (result.size || 50)) : 0;
    const empty = viewingUser
        ? { title: `Nothing assigned to ${shortName(viewingUser)} right now`, message: '' }
        : EMPTY[viewKey] || { title: 'Nothing here', message: '' };
    const monthRows = (calendarData?.rows || []).filter(
        (r) => r.date >= monthStart(month) && r.date <= monthEnd(month),
    ).length;
    const matchText = postingView
        ? postingMatch
        : calendar
        ? calendarData
            ? `${monthRows} task${monthRows === 1 ? '' : 's'} in ${monthName(month)}`
            : null
        : result
          ? `${result.total} task${result.total === 1 ? '' : 's'} match`
          : null;

    const savedItem = (f) => {
        const open = f.id === urlState.saved;
        const more = f.canEdit || f.canLink;
        // The ⋯ button sits beside the entry, not inside it, so it isn't a button within a button
        return (
            <ListItem
                key={f.id}
                disablePadding
                secondaryAction={more ? savedMoreButton(f) : null}
                sx={{ '& .MuiListItemSecondaryAction-root': { right: 2 } }}
            >
                <ListItemButton
                    selected={open}
                    onClick={() => openSavedFilter(f)}
                    sx={{ borderRadius: 1, pr: more ? 5 : 1 }}
                    aria-label={`${f.name}${f.mine ? '' : ', team filter'}${open && edited ? ', edited' : ''}`}
                >
                    <ListItemText
                        primary={f.name}
                        secondary={open && edited ? 'edited' : null}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ fontStyle: 'italic' }}
                    />
                    {!f.mine && <Chip size="small" label="Team" sx={{ height: 18, fontSize: '0.68rem', mx: 0.5 }} />}
                </ListItemButton>
            </ListItem>
        );
    };

    const savedMoreButton = (f) => (
        <IconButton
            size="small"
            aria-label={`More for ${f.name}`}
            aria-haspopup="menu"
            sx={narrow ? { minWidth: 44, minHeight: 44 } : undefined}
            onClick={(e) => setSavedMenu({ anchor: e.currentTarget, filter: f })}
        >
            <MoreVertIcon fontSize="small" />
        </IconButton>
    );

    const tapSize = narrow ? { minWidth: 44, minHeight: 44 } : undefined;
    const count = countText != null && (
        <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {countText}
        </Typography>
    );
    const refreshButton = (
        <Tooltip title="Refresh">
            <IconButton
                size="small"
                aria-label="Refresh"
                sx={tapSize}
                onClick={() => {
                    reload();
                    refreshTaskSummary();
                }}
            >
                <RefreshIcon />
            </IconButton>
        </Tooltip>
    );
    // The only filled button on the toolbar; translators, AV and task viewers get neither
    const newAndMore = (
        <>
            {summary?.canManage && (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    aria-haspopup="menu"
                    aria-expanded={!!newMenu}
                    onClick={(e) => setNewMenu(e.currentTarget)}
                    sx={{ flexShrink: 0, ...tapSize }}
                >
                    New
                </Button>
            )}
            {hasMore && (
                <Tooltip title="More">
                    <IconButton
                        size="small"
                        aria-label="More"
                        aria-haspopup="menu"
                        aria-expanded={!!moreMenu}
                        sx={tapSize}
                        onClick={(e) => setMoreMenu(e.currentTarget)}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    const rail = narrow ? (
        <Box sx={{ display: 'flex', alignItems: phone ? 'center' : 'flex-start', gap: 0.5, mb: 2 }}>
            <TextField
                select
                fullWidth
                size="small"
                label="View"
                value={views ? (openSaved ? `saved:${openSaved.id}` : viewKey) : ''}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith('saved:')) openSavedFilter(savedFilters.find((f) => `saved:${f.id}` === v));
                    else openView(v);
                }}
            >
                {(views || []).map((v) => (
                    <MenuItem key={v.viewKey} value={v.viewKey}>
                        {v.displayName}
                    </MenuItem>
                ))}
                {savedFilters.length > 0 && (
                    // Sticks to the top of the menu as it scrolls, so it needs a background
                    <ListSubheader sx={{ ...RAIL_HEADING, backgroundColor: 'background.paper' }}>Saved</ListSubheader>
                )}
                {savedFilters.map((f) => (
                    <MenuItem key={f.id} value={`saved:${f.id}`}>
                        {f.name}
                        {f.mine ? '' : ' (Team)'}
                        {f.id === urlState.saved && edited ? ' · edited' : ''}
                    </MenuItem>
                ))}
            </TextField>
            {openSaved && (openSaved.canEdit || openSaved.canLink) && savedMoreButton(openSaved)}
            {phone && (
                <>
                    {count}
                    {refreshButton}
                    {newAndMore}
                </>
            )}
        </Box>
    ) : (
        <Box component="nav" aria-label="Task views" sx={{ width: 220, flexShrink: 0, mr: 2 }}>
            {sections.map((section) => (
                <List
                    key={section.title}
                    dense
                    subheader={<ListSubheader sx={RAIL_HEADING}>{section.title}</ListSubheader>}
                >
                    {section.items.map((v) => (
                        <ListItemButton
                            key={v.viewKey}
                            selected={v.viewKey === viewKey && !openSaved}
                            onClick={() => openView(v.viewKey)}
                            sx={{ borderRadius: 1 }}
                        >
                            <ListItemText primary={v.displayName} />
                            {v.viewKey === 'mine' && summary?.openCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {summary.openCount}
                                </Typography>
                            )}
                        </ListItemButton>
                    ))}
                </List>
            ))}
            {savedFilters.length > 0 && (
                <List dense subheader={<ListSubheader sx={RAIL_HEADING}>Saved</ListSubheader>}>
                    {mySaved.map(savedItem)}
                    {teamSaved.map(savedItem)}
                </List>
            )}
        </Box>
    );

    const filterBar = view && chips.length > 0 && (
        <Box ref={filterBarRef} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1.5 }}>
            {chips.map((c, i) => (
                <Chip
                    key={`${c.field}:${c.value}`}
                    data-chip
                    size="small"
                    label={c.label}
                    onDelete={() => removeFilterChip(c, i)}
                    deleteIcon={
                        <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1, padding: '0 4px' }}>
                            ×
                        </span>
                    }
                    // The chip itself is the button: "Remove filter Language: Korean"
                    onClick={() => removeFilterChip(c, i)}
                    aria-label={`Remove filter ${c.label}`}
                />
            ))}
            <Button size="small" onClick={clearAll}>
                Clear all
            </Button>
        </Box>
    );

    // Filters on and nothing anywhere: not in the window, the trays or later
    const calendarNoMatch =
        chips.length > 0 &&
        !!calendarData &&
        calendarData.rows.length === 0 &&
        !calendarData.overdue?.count &&
        !calendarData.noDate?.count &&
        !calendarData.nextDate;
    const noMatch = (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="subtitle1">No tasks match these filters</Typography>
            <Button sx={{ mt: 1 }} onClick={clearAll}>
                Clear all
            </Button>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
            <Box sx={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', p: { xs: 2, md: 3 } }}>
                {rail}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Part 13: the page on top, the list below. A phone keeps the page actions by the View dropdown */}
                    {(!phone || viewingUser) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minWidth: 0 }}>
                            <Typography variant="h5" noWrap title={pageTitle} sx={{ fontWeight: 600, minWidth: 0 }}>
                                {pageTitle}
                            </Typography>
                            {!phone && count}
                            {openSaved && view && (
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                                    on {view.displayName}
                                </Typography>
                            )}
                            {!phone && refreshButton}
                            {viewingUser && (
                                <Button
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                    onClick={() => setUrl({ user: undefined, page: 0 })}
                                >
                                    Back to my tasks
                                </Button>
                            )}
                            <Box sx={{ flex: 1 }} />
                            {!phone && newAndMore}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="Search by file name"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            // Gives way first, from 320px down to 160px, before the row wraps
                            sx={phone ? { flex: '1 1 100%' } : { flex: '1 1 160px', maxWidth: 320 }}
                        />
                        {fields.length > 0 && (
                            <Button
                                ref={filtersButton}
                                size="small"
                                variant="outlined"
                                startIcon={<FilterListIcon />}
                                onClick={(e) => setFiltersAnchor(e.currentTarget)}
                                aria-haspopup="dialog"
                                aria-expanded={!!filtersAnchor}
                                sx={tapSize}
                            >
                                Filters{chips.length > 0 ? ` (${chips.length})` : ''}
                            </Button>
                        )}
                        {board && (
                            <TextField
                                select
                                size="small"
                                label="Show"
                                value={boardScope}
                                onChange={(e) => setBoardScope(e.target.value)}
                                sx={{ width: 170 }}
                            >
                                {BOARD_SCOPES.map((x) => (
                                    <MenuItem key={x.value} value={x.value}>
                                        {x.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {(showGroup || showUpcoming) && (
                            <Button
                                ref={displayButton}
                                size="small"
                                variant="outlined"
                                onClick={(e) => setDisplayAnchor(e.currentTarget)}
                                aria-haspopup="dialog"
                                aria-expanded={!!displayAnchor}
                                sx={tapSize}
                            >
                                Display{displayCount > 0 ? ` (${displayCount})` : ''}
                            </Button>
                        )}
                        {/* Changes which tasks show, so it stays out where it can be seen */}
                        {viewKey === 'needs-attention' && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!urlState.allPms}
                                        onChange={(e) => setUrl({ allPms: e.target.checked || undefined, page: 0 })}
                                    />
                                }
                                label="All PMs"
                            />
                        )}
                        {openSaved && edited && openSaved.canEdit && (
                            <Button size="small" variant="outlined" onClick={() => updateSaved(openSaved)}>
                                Update
                            </Button>
                        )}
                        {openSaved && edited && (
                            <Button
                                size="small"
                                onClick={() => setNaming({ mode: 'new', name: `${openSaved.name} (2)` })}
                            >
                                Save as new
                            </Button>
                        )}
                        {savable && (
                            <Button size="small" onClick={() => setNaming({ mode: 'new', name: '' })}>
                                Save filter
                            </Button>
                        )}
                        {(postingView || BOARD_VIEWS.includes(viewKey) || view?.calendar) && (
                            <Box sx={phone ? { flex: '1 1 100%' } : { ml: 'auto' }}>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    fullWidth={phone}
                                    value={layout}
                                    onChange={(_, val) => {
                                        if (!val) return;
                                        if (postingView)
                                            setUrl({ layout: val === 'list' ? undefined : val, date: undefined });
                                        else setLayout(val);
                                    }}
                                    aria-label="Layout"
                                >
                                    <ToggleButton value="list">List</ToggleButton>
                                    {BOARD_VIEWS.includes(viewKey) && <ToggleButton value="board">Board</ToggleButton>}
                                    {(view?.calendar || postingView) && (
                                        <ToggleButton value="calendar">Calendar</ToggleButton>
                                    )}
                                    {postingView && (
                                        <ToggleButton value="bank">
                                            Post bank{postBankCount != null ? ` (${postBankCount})` : ''}
                                        </ToggleButton>
                                    )}
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </Box>

                    {filterBar}

                    {openSaved?.dropped > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {openSaved.dropped} filter{openSaved.dropped === 1 ? '' : 's'} in this saved view no longer
                            exist
                            {openSaved.dropped === 1 ? 's' : ''}.
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {selectMode && !board && !calendar && !postingView && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={allOnPageSelected}
                                indeterminate={selected.size > 0 && !allOnPageSelected}
                                disabled={pageIds.length === 0}
                                onChange={() => setSelected(allOnPageSelected ? new Set() : new Set(pageIds))}
                                inputProps={{ 'aria-label': 'Select all on this page' }}
                            />
                            <Typography variant="body2" sx={{ flex: '1 1 auto' }}>
                                {selected.size > 0
                                    ? `${selected.size} task${selected.size === 1 ? '' : 's'} selected` +
                                      (selectedRowCount > selected.size
                                          ? ` (${selectedRowCount} rows, one per stage)`
                                          : '')
                                    : 'Select tasks on this page'}
                            </Typography>
                            <Button size="small" variant="contained" disabled={selected.size === 0} onClick={openAssign}>
                                Assign
                            </Button>
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                disabled={selected.size === 0}
                                onClick={() => setConfirmDelete(true)}
                            >
                                Delete
                            </Button>
                            <Button size="small" onClick={exitSelectMode}>
                                Cancel
                            </Button>
                        </Box>
                    )}

                    {(!views || (loading && !postingView && !(calendar ? calendarData : result))) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {view && postingView && (
                        <PostingCalendar
                            filter={filter}
                            tab={layout}
                            onTab={(next) => setUrl({ layout: next === 'list' ? undefined : next, date: undefined })}
                            onOpen={openTask}
                            refresh={postingRefresh}
                            date={urlState.date}
                            categories={categories}
                            filtered={chips.length > 0 || !!urlState.q}
                            onClearAll={clearAll}
                            onError={setError}
                            onMatchText={setPostingMatch}
                            onBankCount={setPostBankCount}
                        />
                    )}

                    {view && calendar && calendarData && calendarNoMatch && noMatch}

                    {view && calendar && calendarData && !calendarNoMatch && (
                        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                            <TaskCalendar
                                data={calendarData}
                                month={month}
                                onMonthChange={setMonth}
                                mode={calendarMode}
                                onModeChange={setCalendarMode}
                                on={calendarOn}
                                onOnChange={noPostingDate ? undefined : (on) => setUrl({ on })}
                                group={viewKey === 'all'}
                                onOpen={(row) => openTask(row)}
                                dateFilterLabel={calendarData.dateFilter ? dateLabel(calendarData.dateFilter) : null}
                            />
                        </Box>
                    )}

                    {view && result && board && taskTypes && (
                        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                            {(summary?.canManage || urlState.upcoming) && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {summary?.canManage ? 'Drag a card to another stage to move it there. ' : ''}
                                    {urlState.upcoming ? "Dashed cards haven't reached that stage yet." : ''}
                                </Typography>
                            )}
                            {result.total > result.rows.length && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    Showing {result.rows.length} of {result.total}. Search or filter by type to see the
                                    rest.
                                </Alert>
                            )}
                            <TaskBoard
                                rows={result.rows.filter((r) => boardTypes.includes(r.contentType))}
                                groups={boardTypes.length === 1 ? result.groups : null}
                                taskTypes={taskTypes.filter((t) => boardTypes.includes(t.contentType))}
                                contentType={filter.contentType}
                                canMove={!!summary?.canManage}
                                onOpen={openTask}
                                onMove={moveTask}
                            />
                        </Box>
                    )}

                    {view && result && !board && !calendar && !postingView && result.total === 0 && chips.length > 0 && noMatch}

                    {view && result && !board && !calendar && !postingView && !(result.total === 0 && chips.length > 0) && (
                        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                            <ListView
                                rowMode="task"
                                taskRows={result.rows}
                                taskColumns={view.schema.columns}
                                // Pipeline returns stage counts even ungrouped (for the board); a list only groups when asked
                                groups={result.groupBy ? result.groups : null}
                                currentUser={me}
                                onTaskOpen={openTask}
                                selectedTaskIds={selectMode ? selected : null}
                                onTaskSelectToggle={selectMode ? toggleSelected : undefined}
                                onGroupOpen={viewKey === 'workload' ? openPersonTasks : undefined}
                                groupOpenLabel="Open their tasks"
                                order={urlState.direction || view.schema.defaultDirection}
                                orderBy={urlState.sort || view.schema.defaultSort}
                                onRequestSort={onSort}
                                emptyTitle={empty.title}
                                emptyMessage={empty.message}
                            />
                            {pageCount > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination
                                        count={pageCount}
                                        page={(urlState.page || 0) + 1}
                                        onChange={(_, p) => setUrl({ page: p - 1 })}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            <TaskFiltersPanel
                anchorEl={filtersAnchor}
                open={!!filtersAnchor}
                onClose={() => {
                    setFiltersAnchor(null);
                    // Clear all in the panel can remove the chip row; focus goes back to Filters
                    setTimeout(() => filtersButton.current?.focus(), 0);
                }}
                fields={fields}
                state={urlState}
                onChange={changeFilters}
                onClearAll={clearAll}
                facets={facets}
                facetsFailed={facetsFailed}
                onRetryFacets={loadFacets}
                categories={categories}
                platforms={platforms}
                matchText={matchText}
            />

            <Popover
                open={!!displayAnchor}
                anchorEl={displayAnchor}
                onClose={() => {
                    setDisplayAnchor(null);
                    setTimeout(() => displayButton.current?.focus(), 0);
                }}
                disableRestoreFocus
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { 'aria-label': 'Display', sx: { p: 2, width: 260 } } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {showGroup && (
                        <TextField
                            select
                            size="small"
                            label="Group by"
                            value={currentGroup}
                            onChange={(e) => setUrl({ group: e.target.value, page: 0 })}
                            fullWidth
                        >
                            {groupOptions.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {GROUP_LABELS[g] || g}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {showUpcoming && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!urlState.upcoming}
                                    onChange={(e) => setUrl({ upcoming: e.target.checked || undefined, page: 0 })}
                                />
                            }
                            label="Show upcoming"
                        />
                    )}
                </Box>
            </Popover>

            <Menu anchorEl={newMenu} open={!!newMenu} onClose={() => setNewMenu(null)}>
                <MenuItem
                    onClick={() => {
                        setNewMenu(null);
                        setNewTaskOpen(true);
                    }}
                >
                    New task
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setNewMenu(null);
                        setNewPostOpen(true);
                    }}
                >
                    New post
                </MenuItem>
            </Menu>

            {/* What's rarely used. Select only works on the list */}
            <Menu anchorEl={moreMenu} open={!!moreMenu} onClose={() => setMoreMenu(null)}>
                {canSelect && (
                    <MenuItem
                        disabled={selectMode}
                        onClick={() => {
                            setMoreMenu(null);
                            setSelectMode(true);
                        }}
                    >
                        Select tasks
                    </MenuItem>
                )}
                {summary?.canManage && <MenuItem onClick={() => navigate('/tasks/tools')}>Task tools</MenuItem>}
            </Menu>

            <Menu anchorEl={savedMenu?.anchor} open={!!savedMenu} onClose={() => setSavedMenu(null)}>
                {savedMenu?.filter.canEdit && (
                    <MenuItem
                        onClick={() => {
                            setNaming({ mode: 'rename', filter: savedMenu.filter, name: savedMenu.filter.name });
                            setSavedMenu(null);
                        }}
                    >
                        Rename
                    </MenuItem>
                )}
                {savedMenu?.filter.canEdit && savedMenu.filter.id === urlState.saved && (
                    <MenuItem
                        disabled={!edited}
                        onClick={() => {
                            updateSaved(savedMenu.filter);
                            setSavedMenu(null);
                        }}
                    >
                        Update to what's on screen
                    </MenuItem>
                )}
                {savedMenu?.filter.canShareWithTeam && (
                    <MenuItem
                        onClick={() => {
                            toggleShared(savedMenu.filter);
                            setSavedMenu(null);
                        }}
                    >
                        {savedMenu.filter.sharedWithTeam ? 'Stop sharing with team' : 'Share with team'}
                    </MenuItem>
                )}
                {savedMenu?.filter.canLink && (
                    <MenuItem
                        onClick={() => {
                            setLinksFor(savedMenu.filter);
                            setSavedMenu(null);
                        }}
                    >
                        {savedMenu.filter.liveLinks > 0 ? `Links (${savedMenu.filter.liveLinks})` : 'Share link'}
                    </MenuItem>
                )}
                {savedMenu?.filter.canEdit && <Divider />}
                {savedMenu?.filter.canEdit && (
                    <MenuItem
                        sx={{ color: 'error.main' }}
                        onClick={() => {
                            setDeleting(savedMenu.filter);
                            setSavedMenu(null);
                        }}
                    >
                        Delete
                    </MenuItem>
                )}
            </Menu>

            <SaveFilterDialog
                open={!!naming}
                title={naming?.mode === 'rename' ? 'Rename saved filter' : 'Save filter'}
                submitLabel={naming?.mode === 'rename' ? 'Rename' : 'Save'}
                initialName={naming?.name || ''}
                canShare={naming?.mode !== 'rename' && !!summary?.canManage}
                onSubmit={naming?.mode === 'rename' ? rename : saveNew}
                onClose={() => setNaming(null)}
            />
            <LiveLinkConfirm
                filter={liveConfirm?.filter}
                action={liveConfirm?.action}
                onClose={() => setLiveConfirm(null)}
                onConfirm={() => {
                    const run = liveConfirm.run;
                    setLiveConfirm(null);
                    run();
                }}
            />
            <ViewLinksDialog
                open={!!linksFor}
                filter={linksFor}
                onClose={() => setLinksFor(null)}
                onChanged={loadSaved}
            />
            <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs">
                <DialogTitle>Delete "{deleting?.name}"?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleting?.sharedWithTeam ? 'It is removed for the whole team. ' : ''}
                        {deleting?.liveLinks > 0 ? 'Its links stop working. ' : ''}
                        The tasks themselves don't change.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(null)}>Cancel</Button>
                    <Button color="error" onClick={deleteSaved}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <TaskDrawer
                open={!!urlState.task}
                taskId={urlState.task}
                focusAction={drawerFocus}
                onClose={() => setUrl({ task: undefined })}
                onChanged={() => reload()}
                onOpenTask={(id) => setUrl({ task: id })}
                setCanNavigateToEdit={setCanNavigateToEdit}
            />
            <NewPostDialog
                open={newPostOpen}
                me={me}
                onClose={() => setNewPostOpen(false)}
                onCreated={() => {
                    reload();
                    refreshTaskSummary();
                }}
            />
            <NewTaskDialog
                open={newTaskOpen}
                onClose={() => setNewTaskOpen(false)}
                onCreated={(taskId) => {
                    reload();
                    refreshTaskSummary();
                    setUrl({ task: taskId });
                }}
            />
            {assigning && (
                <TaskActionDialog
                    open
                    usageName="TaskBulkAssignDialog"
                    title={`Assign ${selected.size} task${selected.size === 1 ? '' : 's'}`}
                    description="A stage already done on a ticked task is left alone."
                    fields={[
                        { name: 'stageKey', label: 'Stage', type: 'select', options: assigning.stages, required: true },
                        { name: 'assignee', label: 'Person', type: 'user', users: assigning.users, required: true },
                    ]}
                    submitLabel="Assign"
                    onSubmit={assignSelected}
                    onClose={() => setAssigning(null)}
                />
            )}
            {confirmDelete && (
                <TaskActionDialog
                    open
                    usageName="TaskBulkDeleteDialog"
                    title={`Delete ${selected.size} task${selected.size === 1 ? '' : 's'}`}
                    description="The tasks and their History are removed for good. Their files and the editor stay as they are."
                    fields={DELETE_FIELDS}
                    submitLabel="Delete"
                    submitColor="error"
                    onSubmit={deleteSelected}
                    onClose={() => setConfirmDelete(false)}
                />
            )}
            <Snackbar
                open={!!snack}
                autoHideDuration={6000}
                onClose={(_, reason) => reason !== 'clickaway' && setSnack(null)}
                message={snack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            />
        </Box>
    );
};

export default TasksPage;
